/* src/views/admin/kitchen/components/OrdersSection.jsx */
import React from 'react';
import { useDispatch } from 'react-redux';
import { updateKitchenOrderStatusService } from '@/services/OrderServices';
import { bucketOf } from '@/utils/orderBuckets';

/* ───── colour tokens ───── */
const STATUS_COLOR = {
  Confirmed: 'bg-[#0A66FF]',
  Preparing: 'bg-[#F79009]',
  Done:      'bg-[#12B76A]',
};
const PALETTE = {
  dine: { tint: '#eaf2ff', ring: 'ring-blue-100',   text: 'text-blue-600'   },
  take: { tint: '#f8f1ff', ring: 'ring-purple-100', text: 'text-purple-600' },
};

/* ───── icons ───── */
// Bell icon (Heroicons outline)
const BellIcon = ({ cls }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={cls}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118.5 14.5V11
         a6.5 6.5 0 00-5-6.294V4a1.5 1.5 0 10-3 0v.706
         A6.5 6.5 0 005.5 11v3.5c0 .133-.052.261-.147.355
         L4 17h5m6 0v1a3 3 0 01-6 0v-1m6 0H9"
    />
  </svg>
);

const Chevron = ({ open }) => (
  <svg
    viewBox="0 0 20 20"
    className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M6 8l4 4 4-4" strokeLinecap="round" />
  </svg>
);

/* helpers */
const shortId  = s => s?.slice(-6) ?? '';
const toUiStat = s =>
  /processing/i.test(s) ? 'Preparing'
  : /(accept|placed|pending)/i.test(s) ? 'Confirmed'
  : 'Done';

/* ───────────────────────────────────────────────────── */
export default function OrdersSection({
  dineIn, takeAway, expanded, onToggle, onStatusUpdated, activeTab,
}) {
  return (
    <div className="grid md:grid-cols-2 gap-6 flex-1 overflow-hidden">
      <Column title="Dine-In Orders" rows={dineIn} palette={PALETTE.dine}
              {...{ expanded, onToggle, onStatusUpdated, activeTab }} />
      <Column title="Takeaway"      rows={takeAway} palette={PALETTE.take}
              {...{ expanded, onToggle, onStatusUpdated, activeTab }} />
    </div>
  );
}

/* ───── column wrapper ───── */
function Column({ title, rows, palette, expanded, onToggle, onStatusUpdated, activeTab }) {
  return (
    <section className="flex flex-col rounded-2xl overflow-hidden">
      <header className="px-4 py-3 border-b font-semibold">{title}</header>

      <div className="flex-1 overflow-y-auto p-4 space-y-4
                      scrollbar-thin scrollbar-thumb-[#c7c9e0]/60">
        {rows
          .filter(o => activeTab === 'ALL' || bucketOf(o.statusName) === activeTab)
          .map(o => (
            <Card
              key={o._id}
              order={o}
              palette={palette}
              open={expanded === o._id}
              toggle={() => onToggle(expanded === o._id ? null : o._id)}
              onStatusUpdated={onStatusUpdated}
            />
          ))}
        {rows.length === 0 && (
          <p className="pt-8 text-center text-gray-400 text-sm">— No orders —</p>
        )}
      </div>
    </section>
  );
}

/* ───── single card ───── */
function Card({ order, palette, open, toggle, onStatusUpdated }) {
  const dispatch = useDispatch();
  const uiStatus = toUiStat(order.statusName);

  const cta = uiStatus === 'Confirmed'
    ? { label: 'Start Preparing', colour: 'bg-[#4F46E5]', next: 4 }
    : uiStatus === 'Preparing'
    ? { label: 'Mark Out for Delivery', colour: 'bg-[#12B76A]', next: 5 }
    : null;

  const bump = async () => {
    if (!cta) return;
    await dispatch(updateKitchenOrderStatusService(order._id, cta.next));
    onStatusUpdated?.();
  };

  return (
    <article
      className={`rounded-xl ring-1 ${palette.ring} bg-white
                  hover:shadow-lg transition-shadow relative`}
    >
      {/* header strip */}
      <div
        onClick={toggle}
        className="flex items-center px-4 py-[10px]"
        style={{
          background: palette.tint,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10
        }}
      >
        <BellIcon cls={`h-5 w-5 ${palette.text}`} />
        <p className={`ml-2 font-medium ${palette.text}`}>#{shortId(order.order_serial_no)}</p>
        <div className="ml-auto flex items-center justify-center">
          <div className="h-7 w-7 rounded-full bg-white ring-1 ring-black/10 shadow-sm flex items-center justify-center">
            <Chevron open={open} />
          </div>
        </div>
      </div>

      {/* meta */}
      <div className="px-4 pt-2 pb-3 cursor-pointer" onClick={toggle}>
        {order.dining_table_id && (
          <p className="text-sm">
            Table <span className="font-semibold">{order.dining_table_id.name}</span>
          </p>
        )}
        {order.token && (
          <p className="text-sm">
            Token <span className="font-semibold">{order.token}</span>
          </p>
        )}
        <p className="text-xs text-gray-500">
          {new Date(order.order_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })},{' '}
          {new Date(order.order_datetime).toLocaleDateString()}
        </p>
      </div>

      {/* status pill */}
      <span className={`${STATUS_COLOR[uiStatus]} absolute top-2 right-2
                        text-white text-[11px] px-2 py-0.5 rounded-full`}>
        {uiStatus}
      </span>

      {/* body */}
      <div className={`grid transition-all duration-300 overflow-hidden ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="min-h-0">
          <div className="divide-y">
            {order.order_items?.map(it => (
              <div key={it._id} className="flex gap-2 py-3 text-sm px-4">
                <span className="w-8 font-semibold">{it.quantity}×</span>
                <div className="flex-1">
                  <p className="font-medium">{it.item_id?.name}</p>
                  {it.size && <p className="text-xs text-gray-500">Size: {it.size}</p>}
                </div>
              </div>
            ))}

            {cta && (
              <div className="p-4">
                <button
                  onClick={bump}
                  className={`w-full h-10 rounded-md font-medium text-white ${cta.colour}
                              hover:opacity-90 transition`}
                >
                  {cta.label}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
